import { OrderActivity } from '../interfaces/order-activity.model';
import { TaskApprovativo, TaskApprovativoRole } from '../interfaces/task-approvativo.model';
import { TaskHistoryStep } from '../lib/task-detail/task-detail.model';

// Step del task in ordine di decisione con il primario per ultimo: su un task chiuso solo quelli decisi
export function visibleSteps(task: TaskApprovativo): TaskApprovativoRole[] {
  const steps = [...(task.roles ?? [])].sort((a, b) => (a.ordinamento ?? 0) - (b.ordinamento ?? 0));
  if (task.primaryRole && !steps.some(s => s.id === task.primaryRole.id)) {
    steps.push(task.primaryRole);
  }
  const closed = steps.some(s => s.outcome === 'Rejected' || s.outcome === 'Modified')
    || task.primaryRole?.outcome === 'Approved';

  return closed ? steps.filter(s => !!s.outcome) : steps;
}

// Storico dagli step del task: la richiesta e gli step, dal piu' recente. Lo step in corso e' in carico dall'ultima decisione,
// o dall'invio
export function historyFromRoles(task: TaskApprovativo): TaskHistoryStep[] {
  const steps = visibleSteps(task);
  const current = steps.find(s => !s.outcome);
  const decided = current ? steps.slice(0, steps.indexOf(current)) : steps;
  const inChargeSince = decided[decided.length - 1]?.dataApprovazione ?? task.dataTask ?? null;
  const request: TaskHistoryStep = {
    date: task.dataTask ?? null,
    user: task.inviatoDa ?? null,
    group: null,
    text: '',
    textKey: 'requestSent',
    role: task.primaryRole?.name ?? null,
    note: null,
    changes: [],
    outcome: 'Start',
    current: false,
    attachments: [],
    taskId: task.id,
  };

  return [request, ...steps.map((s): TaskHistoryStep => ({
    date: s === current ? inChargeSince : s.dataApprovazione ?? null,
    user: historyUser(s.utenteApprovatore?.descrizione, s.name),
    group: historyGroup(s.utenteApprovatore?.descrizione, s.utenteApprovatore?.gruppoUtente),
    text: s.outcome ? s.notes ?? '' : '',
    textKey: null,
    role: s.outcome ? s.name : null,
    note: null,
    changes: [],
    outcome: s.outcome || null,
    current: s === current,
    attachments: s.allegati ?? [],
    taskId: task.id,
  }))].reverse();
}

// Storico dei task nati in App: le attivita' dell'ordine su tutte le sue versioni, dalla piu' recente
export function historyFromActivities(activities: OrderActivity[], taskId: string): TaskHistoryStep[] {
  return activities.map((a): TaskHistoryStep => ({
    date: a.data,
    user: historyUser(a.utente, a.ruoloCodice),
    group: historyGroup(a.utente, a.gruppoUtente),
    text: a.descrizione,
    textKey: null,
    role: a.utente != null && a.ruoloCodice ? a.ruoloCodice : null,
    note: a.nota,
    changes: a.modifiche ?? [],
    outcome: activityOutcome(a),
    current: a.stato === 'InCorso',
    attachments: a.allegati ?? [],
    taskId: a.idTask ?? taskId,
  })).reverse();
}

// Nome di chi ha agito; senza nome il codice del ruolo
function historyUser(name?: string | null, role?: string | null): string | null {
  return name || (role ?? null);
}

// Gruppo di chi ha agito, mostrato solo accanto al nome
function historyGroup(name?: string | null, group?: string | null): string | null {
  return name ? group ?? null : null;
}

// Le attivita' avvenute: approvazioni, rifiuti e le altre voci
function activityOutcome(activity: OrderActivity): string | null {
  if (activity.stato !== 'Fatta') return null;

  switch (activity.tipo) {
    case 'Parere':
    case 'Approvazione':
      return 'Approved';
    case 'Rifiuto':
    case 'Annullamento':
    case 'ErroreErp':
      return 'Rejected';
    default:
      return 'Start';
  }
}
